import { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { format } from "date-fns";
import { Plus, Users, TrendingUp, Copy, CheckCircle, ChevronDown, ChevronUp, LogOut, Gift, UserPlus, CalendarIcon } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { useToast } from "@/hooks/use-toast";
import useAdminAmbassadors from "@/hooks/useAdminAmbassadors";
import useAuth from "@/hooks/useAuth";
import Spinner from "@/components/Spinner";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

interface ReferralCode {
  id: string;
  code: string;
  ambassador_id: string;
  is_active: boolean;
  uses_count: number;
  created_at: string;
}

const AdminAmbassadors = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { signOut } = useAuth();
  const {
    ambassadors,
    allReferrals,
    allPrizeEntries,
    loading,
    isAdmin,
    createAmbassador,
    updateAmbassador,
    generateCodeForAmbassador,
    getAmbassadorReferralCodes,
    getAmbassadorReferrals,
    getAmbassadorPrizeEntries,
  } = useAdminAmbassadors();

  const [showAddDialog, setShowAddDialog] = useState(false);
  const [newName, setNewName] = useState("");
  const [newEmail, setNewEmail] = useState("");
  const [newCommissionRate, setNewCommissionRate] = useState("5.00");
  const [creating, setCreating] = useState(false);
  const [expandedAmbassador, setExpandedAmbassador] = useState<string | null>(null);
  const [ambassadorCodes, setAmbassadorCodes] = useState<Record<string, ReferralCode[]>>({});
  const [loadingCodes, setLoadingCodes] = useState<Record<string, boolean>>({});
  const [copiedCode, setCopiedCode] = useState<string | null>(null);
  const [generatingCode, setGeneratingCode] = useState<string | null>(null);
  const [selectedAmbassadorId, setSelectedAmbassadorId] = useState<string>("all");
  const [startDate, setStartDate] = useState<Date | undefined>(undefined);
  const [endDate, setEndDate] = useState<Date | undefined>(undefined);

  // Filter data by date range
  const filterByDateRange = <T extends { created_at: string | null }>(items: T[]): T[] => {
    return items.filter((item) => {
      if (!item.created_at) return true;
      const itemDate = new Date(item.created_at);
      if (startDate && itemDate < startDate) return false;
      if (endDate) {
        const endOfDay = new Date(endDate);
        endOfDay.setHours(23, 59, 59, 999);
        if (itemDate > endOfDay) return false;
      }
      return true;
    });
  };

  // Calculate stats for selected ambassador or all
  const selectedStats = useMemo(() => {
    const SIGNUP_VALUE = 0.50; // £0.50 per signup

    const filteredReferrals = filterByDateRange(allReferrals);
    const filteredPrizeEntries = filterByDateRange(allPrizeEntries);

    if (selectedAmbassadorId === "all") {
      const signupCount = filteredReferrals.length;
      const signupValue = signupCount * SIGNUP_VALUE;
      const prizeEntries = filteredPrizeEntries.reduce((sum, e) => sum + e.entries, 0);
      const prizeAmbassadorShare = filteredPrizeEntries.reduce((sum, e) => sum + e.ambassador_share, 0) / 100; // pence to £
      return {
        signupCount,
        signupValue,
        prizeEntries,
        prizeAmbassadorShare,
        totalValue: signupValue + prizeAmbassadorShare,
      };
    }

    const referrals = filteredReferrals.filter((r) => r.ambassador_id === selectedAmbassadorId);
    const prizeEntries = filteredPrizeEntries.filter((e) => e.ambassador_id === selectedAmbassadorId);
    const signupCount = referrals.length;
    const signupValue = signupCount * SIGNUP_VALUE;
    const totalPrizeEntries = prizeEntries.reduce((sum, e) => sum + e.entries, 0);
    const prizeAmbassadorShare = prizeEntries.reduce((sum, e) => sum + e.ambassador_share, 0) / 100; // pence to £

    return {
      signupCount,
      signupValue,
      prizeEntries: totalPrizeEntries,
      prizeAmbassadorShare,
      totalValue: signupValue + prizeAmbassadorShare,
    };
  }, [selectedAmbassadorId, allReferrals, allPrizeEntries, startDate, endDate]);

  const clearDateFilters = () => {
    setStartDate(undefined);
    setEndDate(undefined);
  };

  const handleCreateAmbassador = async () => {
    if (!newName.trim() || !newEmail.trim()) {
      toast({
        title: "Error",
        description: "Please fill in all required fields",
        variant: "destructive",
      });
      return;
    }

    setCreating(true);
    const result = await createAmbassador(newName, newEmail, parseFloat(newCommissionRate));
    setCreating(false);

    if (result) {
      toast({
        title: "Success",
        description: `Ambassador ${newName} has been created`,
      });
      setShowAddDialog(false);
      setNewName("");
      setNewEmail("");
      setNewCommissionRate("5.00");
    } else {
      toast({
        title: "Error",
        description: "Failed to create ambassador. Email may already be in use.",
        variant: "destructive",
      });
    }
  };

  const handleToggleActive = async (ambassadorId: string, currentActive: boolean) => {
    const success = await updateAmbassador(ambassadorId, { is_active: !currentActive });
    if (success) {
      toast({
        title: "Updated",
        description: `Ambassador ${!currentActive ? "activated" : "deactivated"}`,
      });
    }
  };

  const handleExpandAmbassador = async (ambassadorId: string) => {
    if (expandedAmbassador === ambassadorId) {
      setExpandedAmbassador(null);
      return;
    }

    setExpandedAmbassador(ambassadorId);

    if (!ambassadorCodes[ambassadorId]) {
      setLoadingCodes((prev) => ({ ...prev, [ambassadorId]: true }));
      const codes = await getAmbassadorReferralCodes(ambassadorId);
      setAmbassadorCodes((prev) => ({ ...prev, [ambassadorId]: codes }));
      setLoadingCodes((prev) => ({ ...prev, [ambassadorId]: false }));
    }
  };

  const handleGenerateCode = async (ambassadorId: string) => {
    setGeneratingCode(ambassadorId);
    const newCode = await generateCodeForAmbassador(ambassadorId);
    setGeneratingCode(null);

    if (newCode) {
      setAmbassadorCodes((prev) => ({
        ...prev,
        [ambassadorId]: [newCode, ...(prev[ambassadorId] || [])],
      }));
      toast({
        title: "Code Generated",
        description: `New referral code: ${newCode.code}`,
      });
    } else {
      toast({
        title: "Error",
        description: "Failed to generate code",
        variant: "destructive",
      });
    }
  };

  const copyToClipboard = async (code: string) => {
    const referralLink = `https://cozystamp.com/?ref=${code}`;
    await navigator.clipboard.writeText(referralLink);
    setCopiedCode(code);
    toast({
      title: "Copied!",
      description: "Referral link copied to clipboard",
    });
    setTimeout(() => setCopiedCode(null), 2000);
  };

  const handleSignOut = async () => {
    await signOut();
    navigate("/");
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Spinner size={32} />
      </div>
    );
  }

  if (!isAdmin) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-background p-6">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <CardTitle>Access Denied</CardTitle>
            <CardDescription>
              You don't have admin access to manage ambassadors.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button onClick={() => navigate("/")} variant="outline" className="w-full">
              Go Home
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const totalReferrals = allReferrals.length;
  const totalEarnings = allReferrals.reduce((sum, r) => sum + Number(r.commission_amount), 0);
  const activeAmbassadors = ambassadors.filter((a) => a.is_active).length;

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b border-border bg-card">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-foreground">Ambassador Management</h1>
            <p className="text-sm text-muted-foreground">Manage your ambassador program</p>
          </div>
          <Button variant="ghost" size="sm" onClick={handleSignOut}>
            <LogOut className="h-4 w-4 mr-2" />
            Sign Out
          </Button>
        </div>
      </header>

      <main className="container mx-auto px-4 py-8 space-y-8">
        {/* Summary Stats */}
        <div className="grid gap-4 md:grid-cols-3">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium">Active Ambassadors</CardTitle>
              <Users className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{activeAmbassadors}</div>
              <p className="text-xs text-muted-foreground">of {ambassadors.length} total</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium">Total Referrals</CardTitle>
              <TrendingUp className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{totalReferrals}</div>
              <p className="text-xs text-muted-foreground">Users referred</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium">Total Commission</CardTitle>
              <TrendingUp className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">£{totalEarnings.toFixed(2)}</div>
              <p className="text-xs text-muted-foreground">Owed to ambassadors</p>
            </CardContent>
          </Card>
        </div>

        {/* Ambassador Stats Dropdown */}
        <Card>
          <CardHeader>
            <CardTitle>Ambassador Stats</CardTitle>
            <CardDescription>View signups and prize draw entries by ambassador</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex flex-wrap gap-4 items-end">
              <div className="space-y-2">
                <Label className="text-sm text-muted-foreground">Ambassador</Label>
                <Select value={selectedAmbassadorId} onValueChange={setSelectedAmbassadorId}>
                  <SelectTrigger className="w-full md:w-[200px]">
                    <SelectValue placeholder="Select ambassador" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Ambassadors</SelectItem>
                    {ambassadors.map((ambassador) => (
                      <SelectItem key={ambassador.id} value={ambassador.id}>
                        {ambassador.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label className="text-sm text-muted-foreground">Start Date</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className={cn(
                        "w-[160px] justify-start text-left font-normal",
                        !startDate && "text-muted-foreground"
                      )}
                    >
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {startDate ? format(startDate, "dd/MM/yyyy") : "From"}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={startDate}
                      onSelect={setStartDate}
                      initialFocus
                      className="pointer-events-auto"
                    />
                  </PopoverContent>
                </Popover>
              </div>

              <div className="space-y-2">
                <Label className="text-sm text-muted-foreground">End Date</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className={cn(
                        "w-[160px] justify-start text-left font-normal",
                        !endDate && "text-muted-foreground"
                      )}
                    >
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {endDate ? format(endDate, "dd/MM/yyyy") : "To"}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={endDate}
                      onSelect={setEndDate}
                      initialFocus
                      className="pointer-events-auto"
                    />
                  </PopoverContent>
                </Popover>
              </div>

              {(startDate || endDate) && (
                <Button variant="ghost" size="sm" onClick={clearDateFilters}>
                  Clear dates
                </Button>
              )}
            </div>

            <div className="grid gap-4 md:grid-cols-3">
              <Card className="border-border/50">
                <CardHeader className="flex flex-row items-center justify-between pb-2">
                  <CardTitle className="text-sm font-medium">Signups</CardTitle>
                  <UserPlus className="h-4 w-4 text-muted-foreground" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">{selectedStats.signupCount}</div>
                  <p className="text-xs text-muted-foreground">
                    £{selectedStats.signupValue.toFixed(2)} value
                  </p>
                </CardContent>
              </Card>

              <Card className="border-border/50">
                <CardHeader className="flex flex-row items-center justify-between pb-2">
                  <CardTitle className="text-sm font-medium">Prize Draw Entries</CardTitle>
                  <Gift className="h-4 w-4 text-muted-foreground" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">{selectedStats.prizeEntries}</div>
                  <p className="text-xs text-muted-foreground">
                    £{selectedStats.prizeAmbassadorShare.toFixed(2)} share
                  </p>
                </CardContent>
              </Card>

              <Card className="border-border/50">
                <CardHeader className="flex flex-row items-center justify-between pb-2">
                  <CardTitle className="text-sm font-medium">Total Value</CardTitle>
                  <TrendingUp className="h-4 w-4 text-muted-foreground" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">£{selectedStats.totalValue.toFixed(2)}</div>
                  <p className="text-xs text-muted-foreground">Signups + prize share</p>
                </CardContent>
              </Card>
            </div>
          </CardContent>
        </Card>

        {/* Ambassadors List */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <div>
              <CardTitle>Ambassadors</CardTitle>
              <CardDescription>Manage your ambassador accounts</CardDescription>
            </div>
            <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
              <DialogTrigger asChild>
                <Button>
                  <Plus className="h-4 w-4 mr-2" />
                  Add Ambassador
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Add New Ambassador</DialogTitle>
                  <DialogDescription>
                    Create a new ambassador account. They will be able to generate referral codes.
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-4 py-4">
                  <div className="space-y-2">
                    <Label htmlFor="name">Name</Label>
                    <Input
                      id="name"
                      value={newName}
                      onChange={(e) => setNewName(e.target.value)}
                      placeholder="John Doe"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="email">Email</Label>
                    <Input
                      id="email"
                      type="email"
                      value={newEmail}
                      onChange={(e) => setNewEmail(e.target.value)}
                      placeholder="john@example.com"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="commission">Commission Rate (£ per signup)</Label>
                    <Input
                      id="commission"
                      type="number"
                      step="0.01"
                      min="0"
                      value={newCommissionRate}
                      onChange={(e) => setNewCommissionRate(e.target.value)}
                    />
                  </div>
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setShowAddDialog(false)}>
                    Cancel
                  </Button>
                  <Button onClick={handleCreateAmbassador} disabled={creating}>
                    {creating ? <Spinner size={16} /> : "Create Ambassador"}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </CardHeader>
          <CardContent>
            {ambassadors.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <p>No ambassadors yet. Add your first ambassador to get started!</p>
              </div>
            ) : (
              <div className="space-y-4">
                {ambassadors.map((ambassador) => {
                  const ambassadorReferrals = getAmbassadorReferrals(ambassador.id);
                  const isExpanded = expandedAmbassador === ambassador.id;

                  return (
                    <Collapsible
                      key={ambassador.id}
                      open={isExpanded}
                      onOpenChange={() => handleExpandAmbassador(ambassador.id)}
                    >
                      <div className="border border-border rounded-lg overflow-hidden">
                        <CollapsibleTrigger asChild>
                          <div className="flex items-center justify-between p-4 cursor-pointer hover:bg-muted/50">
                            <div className="flex-1">
                              <div className="flex items-center gap-3">
                                <div>
                                  <p className="font-medium">{ambassador.name}</p>
                                  <p className="text-sm text-muted-foreground">{ambassador.email}</p>
                                </div>
                                {!ambassador.is_active && (
                                  <span className="text-xs bg-destructive/10 text-destructive px-2 py-1 rounded">
                                    Inactive
                                  </span>
                                )}
                              </div>
                            </div>
                            <div className="flex items-center gap-4">
                              <div className="text-right text-sm">
                                <p className="font-medium">{ambassadorReferrals.length} referrals</p>
                                <p className="text-muted-foreground">
                                  £{Number(ambassador.total_earnings).toFixed(2)} earned
                                </p>
                              </div>
                              <Switch
                                checked={ambassador.is_active}
                                onCheckedChange={() =>
                                  handleToggleActive(ambassador.id, ambassador.is_active)
                                }
                                onClick={(e) => e.stopPropagation()}
                              />
                              {isExpanded ? (
                                <ChevronUp className="h-4 w-4 text-muted-foreground" />
                              ) : (
                                <ChevronDown className="h-4 w-4 text-muted-foreground" />
                              )}
                            </div>
                          </div>
                        </CollapsibleTrigger>

                        <CollapsibleContent>
                          <div className="border-t border-border p-4 bg-muted/30">
                            <div className="flex items-center justify-between mb-4">
                              <h4 className="font-medium">Referral Codes</h4>
                              <Button
                                size="sm"
                                onClick={() => handleGenerateCode(ambassador.id)}
                                disabled={generatingCode === ambassador.id}
                              >
                                {generatingCode === ambassador.id ? (
                                  <Spinner size={14} />
                                ) : (
                                  <>
                                    <Plus className="h-3 w-3 mr-1" />
                                    Generate
                                  </>
                                )}
                              </Button>
                            </div>

                            {loadingCodes[ambassador.id] ? (
                              <div className="flex justify-center py-4">
                                <Spinner size={20} />
                              </div>
                            ) : (ambassadorCodes[ambassador.id] || []).length === 0 ? (
                              <p className="text-sm text-muted-foreground text-center py-4">
                                No referral codes yet
                              </p>
                            ) : (
                              <div className="space-y-2">
                                {(ambassadorCodes[ambassador.id] || []).map((code) => (
                                  <div
                                    key={code.id}
                                    className="flex items-center justify-between p-3 bg-background rounded border border-border"
                                  >
                                    <div>
                                      <p className="font-mono font-bold">{code.code}</p>
                                      <p className="text-xs text-muted-foreground">
                                        {code.uses_count} uses
                                      </p>
                                    </div>
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      onClick={() => copyToClipboard(code.code)}
                                    >
                                      {copiedCode === code.code ? (
                                        <CheckCircle className="h-4 w-4 text-green-500" />
                                      ) : (
                                        <Copy className="h-4 w-4" />
                                      )}
                                    </Button>
                                  </div>
                                ))}
                              </div>
                            )}

                            <div className="mt-4 pt-4 border-t border-border">
                              <p className="text-sm text-muted-foreground">
                                Commission rate: £{Number(ambassador.commission_rate).toFixed(2)} per signup
                              </p>
                              <p className="text-sm text-muted-foreground">
                                Created: {new Date(ambassador.created_at).toLocaleDateString()}
                              </p>
                            </div>
                          </div>
                        </CollapsibleContent>
                      </div>
                    </Collapsible>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>
      </main>
    </div>
  );
};

export default AdminAmbassadors;
